/**
 * The damage event, end to end.
 *
 * `EffectEvent.Activate()` in four steps (EffectEvent.java:181-209): seed the numbers, sweep
 * both sides' stats in priority order, flush the layers, then expand whatever conversion
 * produced into fresh events. This file is the driver; the arithmetic lives in `layers.ts`,
 * the interpretation in `effects.ts` / `conditions.ts` / `code-only-effects.ts`.
 *
 * ## Crit is branched, not averaged
 *
 * `critical_damage` writes a MULTIPLY layer gated on the `crit` boolean, and `non_crit_damage`
 * writes a different one gated on its negation. Averaging the two gates produces a number the
 * game cannot produce for either outcome. So the whole pipeline runs twice with `crit` pinned,
 * and the weighted mean of the two results is reported alongside them — which is also what a
 * player means by "my DPS".
 *
 * ## What the sweep reads
 *
 * The source side reads the **spell unit** (`calculate` with `skill` set: support gems and the
 * spell's innate stats folded in). The base damage number does *not* — `ScalingCalc` reads
 * `Load.Unit(en).getUnit()`, the plain character sheet. Both sheets are therefore computed and
 * they are not interchangeable.
 */
import { CATEGORY, CODE_ONLY_TRANSFERS, ELEMENTS, SINGLE_ELEMENTS, entry, isDualWielding } from "@cte2/schema";
import { balance } from "../balance.js";
import { calculate } from "../calculate.js";
import { ORIGINAL_MODE } from "../compat.js";
import { maxSpellLevel, spellLevel } from "../collect/spell.js";
import { statIndex } from "../stat-def.js";
import { applyAilments } from "./ailments.js";
import { Recorder } from "./breakdown.js";
import { evaluateIfs } from "./conditions.js";
import { applyStatEffect } from "./effects.js";
import { multiplyExact, parseRolledMods, rollToExact } from "../modifier.js";
import { mobAffixDiagnostics, mobAffixMods } from "./mob-affixes.js";
import { mapMobMods } from "./map.js";
import { activeOn, stacksHeldBy } from "./effect-state.js";
import { inCodeEffects, MAX_CONVERSION_DEPTH } from "./code-only-effects.js";
import { sheetValue } from "./ctx.js";
import { DamageEventState, EVENT } from "./event.js";
import { LAYER, layerIndex } from "./layers.js";
import { PRIORITY, UNKNOWN_ORDER_PRIORITY, datapackPriority } from "./priority.js";
import { calculatedValue, damageEffectiveness, valueCalc } from "./value-calc.js";
/** `DamageEvent.ID` — the event GUID a datapack `effect` block must list to take part. */
export const ON_DAMAGE = "on_damage";
/**
 * `DamageInitEvent.ID` — the pass `initBeforeActivating()` runs before the hit's own sweep.
 *
 * One stat in this pack answers it, `no_attacker_stats_on_selfdmg`, and what it does there could
 * not be done anywhere else: it decides whether the attacker's side of the sweep is assembled.
 */
export const ON_DAMAGE_INIT = "on_damage_init";
export function simulateHit(build, snapshot, options = {}) {
    const diagnostics = [];
    const report = (severity, code, path, message) => {
        diagnostics.push({ severity, code, path, message });
    };
    const skill = options.skill ?? mainSkill(build, report);
    if (!skill) {
        report("error", "no-skill", "skills", "This build declares no skill, so there is no damage to compute.");
        return undefined;
    }
    const spell = entry(snapshot, CATEGORY.spell, skill.spellId)?.data;
    if (!spell) {
        report("error", "unknown-spell", "skills", `No ${CATEGORY.spell} entry \`${skill.spellId}\`.`);
        return undefined;
    }
    const index = statIndex(snapshot);
    const layers = layerIndex(snapshot);
    const bal = balance(snapshot, options.balanceId);
    const compat = options.compat ?? ORIGINAL_MODE;
    const baseOptions = pick(options);
    // Two sheets, deliberately. The character sheet feeds `value_calc`; the spell unit feeds the
    // event's source-side sweep. Both are built with the same effects up as the gates and the
    // mob's debuffs below — a buff that is off here has to be off everywhere, which is the whole
    // reason the state is resolved once and handed down rather than asked for three times.
    const sheetOptions = {
        ...baseOptions,
        ...(options.effects === undefined ? {} : { effects: options.effects }),
    };
    const characterRun = options.sheets?.character ?? calculate(build, snapshot, sheetOptions);
    const characterSheet = characterRun.stats;
    const spellRun = options.sheets?.spell ?? calculate(build, snapshot, { ...sheetOptions, skill });
    // Only when this call computed them: a caller passing sheets in has already collected these.
    if (options.sheets === undefined)
        diagnostics.push(...spellRun.diagnostics);
    const sourceSheet = spellRun.stats;
    const effects = options.effects ?? characterRun.effects;
    // A self-hit lands on you, so the mitigation layers read your sheet. The enemy's debuffs are
    // not applied to it: `shred` and `elemental_weakness` are things you put on the mob, and
    // carrying them across would have your own recoil shredding you.
    const selfHit = options.selfHit === true;
    // Collected unconditionally: it is one map of the stats a debuff or an affix touched, which
    // is a handful of entries, and the Damage tab always wants to be able to explain one.
    const targetOrigins = new Map();
    const targetSheet = selfHit
        ? characterSheet
        : targetSheetFor(build, effects, snapshot, index, bal, report, targetOrigins);
    const act = damageAct(spell, options);
    if (!act) {
        report("warning", "no-damage-action", `skills`, `\`${skill.spellId}\` declares no \`damage\` action, so it deals no direct damage. Its output is entirely procs, ailments or summons, none of which this figure covers.`);
    }
    const env = { snapshot, balance: bal, level: build.character.level };
    const level = spellLevel(envOf(env, index, bal), skill.spellId, spell, skill.level);
    const maxLevel = maxSpellLevel(envOf(env, index, bal), spell);
    const calcId = options.valueCalcId ?? act?.valueCalcId ?? "";
    const calc = calcId.length > 0 ? valueCalc(snapshot, calcId) : undefined;
    if (calcId.length > 0 && !calc) {
        report("error", "unknown-value-calc", "skills", `No ${CATEGORY.valueCalc} entry \`${calcId}\`.`);
    }
    const read = (sheet) => (statId) => sheetValue(sheet, statId);
    const calculated = calc
        ? calculatedValue(calc, read(characterSheet), read(targetSheet), level, maxLevel, build.character.level, bal, compat)
        : 0;
    // `value *= getStacks(id)` on the int the calc returned, before the event is built.
    const stackEffect = options.multiplyByCasterStacks;
    const stackMultiplier = stackEffect === undefined
        ? undefined
        : { effectId: stackEffect, stacks: stacksHeldBy(effects, stackEffect, "caster") };
    const baseValue = stackMultiplier === undefined ? calculated : calculated * stackMultiplier.stacks;
    const effectiveness = calc ? damageEffectiveness(calc, level, maxLevel) : 1;
    const element = options.element ?? act?.element ?? "Physical";
    const critChance = clamp01(sheetValue(sourceSheet, "critical_hit") / 100);
    const shared = {
        snapshot,
        index,
        layers,
        balance: bal,
        compat,
        build,
        skill,
        spell,
        sourceSheet,
        targetSheet,
        baseValue,
        effectiveness,
        element,
        effects,
        breakdown: options.breakdown === true,
        basicAttack: false,
        summonAttack: options.summonAttack === true,
        // `ofSpellDamage` takes the style from `spell.getConfig().getStyle()`, not from the weapon:
        // a caster swinging a dagger is still casting an `int` spell. Unset, `style_is_int_is_false`
        // read true for every hit, which handed thirteen attack-only stats — `attack_damage`,
        // `lifesteal`, `elemental_attack_damage` and four attack procs — to INT spells that the game
        // does not give them to.
        style: spellStyle(spell),
        selfHit,
        sourceStatsDisabled: false,
        hitChance: 1,
    };
    const procBranches = options.procs === true ? { hit: new Map(), crit: new Map() } : undefined;
    const hit = runBranch(shared, false, diagnostics, procBranches?.hit);
    const crit = runBranch(shared, true, diagnostics, procBranches?.crit);
    // `critical_hit` sets `EventData.CRIT` from the **Source** side, so a hit whose attacker sweep
    // `disable_attacker_stats` removed never rolls one. Reporting the sheet's chance there would
    // put a crit column on a self-hit that the game can only ever resolve as a normal one.
    const rolledCrit = shared.sourceStatsDisabled ? 0 : critChance;
    const procs = procBranches === undefined ? undefined : procsPerHit(procBranches.hit, procBranches.crit, rolledCrit);
    return {
        spellId: skill.spellId,
        baseValue,
        ...(stackMultiplier === undefined ? {} : { stackMultiplier }),
        dmgEffectiveness: effectiveness,
        element,
        critChance: rolledCrit,
        hitChance: shared.hitChance,
        selfHit,
        hit,
        crit,
        average: blend(hit, crit, rolledCrit),
        sheets: { character: characterRun, spell: spellRun },
        target: { sheet: targetSheet, origins: targetOrigins },
        ...(procs === undefined ? {} : { procs }),
        diagnostics,
    };
}
function runBranch(shared, crit, diagnostics, procs) {
    const byElement = new Map();
    const ailments = [];
    // One list for the whole branch, bonus-element events included: a converted half is a real
    // `DamageEvent` that runs the source sweep of its own, so `fire_mana_leech` leeches off the
    // fire half of a physical hit exactly as it would off a fire one.
    const restores = [];
    // Both branches report, but only once per distinct diagnostic: the two runs differ solely in
    // a pinned boolean, so nearly every message is identical in both, and an earlier version
    // dropped the non-crit branch's entirely to avoid the duplication. That also dropped the few
    // that are genuinely branch-specific — a crit-only layer being averaged, for one.
    const sink = [];
    const trace = runEvent(shared, shared.element, shared.baseValue, crit, 0, false, byElement, ailments, sink, procs, restores, "0");
    mergeDiagnostics(diagnostics, sink);
    let total = 0;
    for (const amount of byElement.values())
        total += amount;
    return { total, byElement, ailments, restores, ...(trace ? { trace } : {}) };
}
/**
 * One entry per proc for the whole hit, crit already folded in.
 *
 * Every element of a hit rolls its own crit. `buildBonusElementEvent` copies a long list of
 * flags onto the child — `IS_BLOCKED`, `AVOIDANCE_ROLLED`, `IS_SUMMON_ATTACK` — but not
 * `EventData.CRIT`, and `critical_hit`'s `set_bool_crit` gates only on `random_roll` and
 * `attack_type_is_dot_is_false`, which `bonus_dmg` passes (6.4.13 jar and pack). So each
 * element is its own crit roll and its own proc roll:
 *
 *     per event   q = (1 - c)·p_hit + c·p_crit
 *     per hit     1 - Π(1 - q)
 *
 * Blending crit once for the whole hit instead — `c·(1 - Π(1 - p_crit))` — pinned every element
 * to the root's crit and undercounted an `is_crit_true` proc on a multi-element hit: at 50% crit
 * with a certain roll and three elements that is 50% where the game gives 87.5%. Curse of
 * Tongues' `proc_blood_explosion_on_crit` is the case it was found on. A proc that ignores crit
 * has `p_hit = p_crit` and comes out exactly as before.
 *
 * A `damage_taken_as` child never rolls: its attacker sweep is off, so `set_bool_crit` never
 * runs and its `CRIT` stays false. Its non-crit run is the only true one.
 */
function procsPerHit(hit, crit, critChance) {
    const out = new Map();
    for (const key of new Set([...hit.keys(), ...crit.keys()])) {
        const normal = hit.get(key);
        const critted = crit.get(key);
        const c = (normal ?? critted).rollsCrit ? critChance : 0;
        const chances = new Map();
        const fold = (hits, branch) => {
            for (const proc of hits ?? []) {
                const id = `${proc.statId}:${proc.spellId}:${proc.side}`;
                const seen = chances.get(id) ?? { hit: proc, normal: 0, crit: 0 };
                seen[branch] = 1 - (1 - seen[branch]) * (1 - proc.chance);
                chances.set(id, seen);
            }
        };
        fold(normal?.hits, "normal");
        fold(critted?.hits, "crit");
        for (const [id, { hit: proc, normal: p, crit: k }] of chances) {
            const q = (1 - c) * p + c * k;
            const seen = out.get(id);
            if (seen === undefined)
                out.set(id, { ...proc, chance: q });
            else
                seen.chance = 1 - (1 - seen.chance) * (1 - q);
        }
    }
    return [...out.values()];
}
/** Appends the diagnostics the other branch has not already reported, keyed on code and path. */
function mergeDiagnostics(into, from) {
    const seen = new Set(into.map((d) => `${d.code}|${d.path}|${d.message}`));
    for (const d of from) {
        const key = `${d.code}|${d.path}|${d.message}`;
        if (seen.has(key))
            continue;
        seen.add(key);
        into.push(d);
    }
}
/**
 * One `DamageEvent`, including the bonus-element events it spawns.
 *
 * `takenAs` mirrors `DamageEvent.buildBonusElementEvent`: a `DAMAGE_TAKEN_AS` child skips the
 * source-side sweep entirely, because that damage has already been fully multiplied and
 * sweeping the attacker again would apply every offensive stat twice.
 */
function runEvent(shared, element, amount, crit, depth, takenAs, byElement, ailments, diagnostics, procs, restores, 
/** This event's place in the tree, which pairs it with its run in the other branch. */
key) {
    if (depth > MAX_CONVERSION_DEPTH)
        return undefined;
    const ownProcs = procs === undefined ? undefined : [];
    if (procs !== undefined && ownProcs !== undefined)
        procs.set(key, { hits: ownProcs, rollsCrit: !takenAs });
    const recorder = shared.breakdown ? new Recorder() : undefined;
    const event = new DamageEventState(shared.layers, undefined, recorder);
    event.conversionDepth = depth;
    event.data.setupNumber(EVENT.NUMBER, amount);
    event.data.setupNumber(EVENT.DMG_EFFECTIVENESS, shared.effectiveness);
    event.data.setString(EVENT.ELEMENT, element);
    event.data.setString(EVENT.ATTACK_TYPE, depth === 0 ? "hit" : "bonus_dmg");
    event.data.setString(EVENT.SPELL, shared.skill.spellId);
    event.data.setString(EVENT.WEAPON_TYPE, weaponType(shared));
    event.data.setBoolean(EVENT.CRIT, crit);
    // `setupDamage(AttackType.hit, weptype, style)` sets the style on every damage event; only a
    // weapon swing also sets the basic-attack flag, and only at depth 0 — a converted bonus
    // element is `bonus_dmg`, which `BonusAttackDamage` refuses alongside the flag.
    if (shared.style.length > 0)
        event.data.setString(EVENT.STYLE, shared.style);
    if (shared.basicAttack)
        event.data.setBoolean(EVENT.IS_BASIC_ATTACK, true);
    if (shared.summonAttack)
        event.data.setBoolean(EVENT.IS_SUMMON_ATTACK, true);
    if (depth > 0)
        event.data.setBoolean(EVENT.IS_BONUS_ELEMENT_DAMAGE, true);
    // The weapon's own basic-attack multiplier, which `initBeforeActivating` registers before any
    // stat is swept:
    //
    //     if (this.data.isBasicAttack()) {
    //         if (this.attackInfo != null && attackInfo.weaponData != null) {
    //             if (!data.getBoolean(EventData.UNARMED_ATTACK)) {
    //                 float multi = attackInfo.weaponData.GetBaseGearType().getGearSlot().getBasicDamageMulti();
    //                 this.wepdmgMulti = multi;
    //                 this.addMoreMulti(() -> Words.WEAPON_BASIC_ATTACK_DMG_MULTI.locName(), EventData.NUMBER, multi);
    //             }
    //         }
    //     }
    //
    // It is a property of the *slot*, not of the item: `GearSlot.getBasicDamageMulti()` returns
    // `weapon_data.damage_multiplier`, which ranges from 0.9 on a gauntlet to 2.8 on a crossbow.
    // Nothing in the engine applied it, so every swing in this planner was wrong by that factor —
    // 1.6 on the axe this was found with, and the only reason it went unnoticed is that a basic
    // attack had no rate at all until last session and so was reported as zero anyway.
    //
    // `!takenAs`, not `depth === 0`: the game re-applies it to every converted element as well.
    // `buildBonusElementEvent` builds the child from the parent's own `attackInfo`
    //
    //     DamageEvent bonus = EventBuilder.ofDamage(attackInfo, source, target, amount)
    //     …
    //     x.data.setBoolean(EventData.IS_BASIC_ATTACK, this.data.getBoolean(EventData.IS_BASIC_ATTACK));
    //     …
    //     if (!takenAs) { bonus.initBeforeActivating(); }
    //
    // so both conditions the multi is behind — `isBasicAttack()` and a non-null
    // `attackInfo.weaponData` — hold on the child, and `initBeforeActivating` runs the whole block
    // again. (The `addMoreMulti` next to `if (wepdmgMulti != 1)` in that builder is commented out
    // for the same reason: it would have applied it twice.) A damage-*taken*-as child is the one
    // that skips it, because it has already been through every attacker multiplier on the parent.
    //
    // On a converting basic-attack build the old guard under-reported every converted element by
    // the full slot multiplier — 1.6× on an axe, 2.8× on a crossbow.
    if (shared.basicAttack && !takenAs) {
        const multi = basicAttackWeaponMulti(shared);
        // An unarmed swing gets none of it, matching the `UNARMED_ATTACK` guard. A build with no
        // weapon also has no `weapon_damage`, so it is already dealing nothing.
        if (multi !== undefined)
            event.addMoreMulti(WEAPON_BASIC_ATTACK_MULTI, EVENT.NUMBER, multi);
    }
    const ctx = {
        snapshot: shared.snapshot,
        index: shared.index,
        balance: shared.balance,
        compat: shared.compat,
        event,
        source: shared.sourceSheet,
        target: shared.targetSheet,
        sourceLevel: shared.build.character.level,
        targetLevel: shared.selfHit
            ? shared.build.character.level
            : (shared.build.config?.enemy?.level ?? shared.build.config?.enemyLevel ?? shared.build.character.level),
        spell: shared.spell,
        spellId: shared.skill.spellId,
        spellTags: spellTags(shared.spell),
        config: shared.build.config ?? {},
        effects: shared.effects,
        ...(ownProcs === undefined ? {} : { procs: ownProcs }),
        restores,
        diagnostics,
        report: (severity, code, path, message) => diagnostics.push({ severity, code, path, message }),
        reportedConditions: new Set(),
        reportedEffects: new Set(),
        // Pinning `crit` is what makes this a branch rather than a re-roll: `critical_hit`'s own
        // `set_bool` at priority 0 is skipped, so the boolean stays as the caller set it.
        pinnedBooleans: new Set([EVENT.CRIT]),
        disableSourceStats: takenAs,
        sourceIsTarget: shared.selfHit,
        sourceDualWielding: isDualWielding(shared.snapshot, shared.build.gear ?? []),
        ...(shared.selfHit ? { targetDualWielding: isDualWielding(shared.snapshot, shared.build.gear ?? []) } : {}),
    };
    // `initBeforeActivating()` raises a whole `DamageInitEvent` before the hit's own effect list is
    // built, and `DisableSourceStatsEffect` is the only thing in this pack that answers it:
    //
    //     if (event instanceof DamageInitEvent e) { e.dmg.calcSourceEffects = false; }
    //
    // The ordering is the point. `EffectEvent` decides whether to add the source side *after* the
    // init event has run, so a flag set there removes the attacker from the sweep entirely rather
    // than being one more effect inside it. No layer exists yet, so the pass does not flush.
    if (!takenAs) {
        sweep(ctx, [{ side: "Source", sheet: shared.sourceSheet }], [], [], ON_DAMAGE_INIT, false);
        if (depth === 0 && ctx.disableSourceStats)
            shared.sourceStatsDisabled = true;
    }
    const steps = [];
    const moreMultis = [];
    sweep(ctx, [
        // `takenAs` is `DisableSourceStatsEffect`: the attacker's sweep is skipped entirely for the
        // converted half of a hit, because it has already been applied to the original. So is a
        // self-hit, by the same flag — set by `no_attacker_stats_on_selfdmg` in the pass above.
        ...(takenAs || ctx.disableSourceStats ? [] : [{ side: "Source", sheet: shared.sourceSheet }]),
        { side: "Target", sheet: shared.targetSheet },
    ], steps, moreMultis);
    /*
      What fraction of this cast's hits actually land, off the root event.
  
      `damage_block` is the layer dodge and spell dodge average themselves onto, so its multiplier
      *is* the chance to hit — 1 on the hits nothing can evade, which is most of them. Read at
      depth 0 only: a bonus-element child carries `bonus_dmg`, which dodge refuses outright, so a
      child would always report 1 and overwrite the parent's real figure.
  
      Kept on `shared` for the same reason `sourceStatsDisabled` is: it is one fact about the cast
      that the caller needs and the event that knows it is three frames down.
    */
    if (depth === 0)
        shared.hitChance = event.hitChance;
    // The children have to be collected before the trace is closed, because a bonus element is a
    // whole nested event and its own layers belong under this one.
    const children = collectBonusElements(ctx, event, shared, crit, depth, byElement, ailments, diagnostics, procs, restores, key);
    const dealt = Math.max(0, event.damage);
    byElement.set(element, (byElement.get(element) ?? 0) + dealt);
    /*
      Every event that sweeps the attacker rolls its own ailment, not just the hit itself.
  
      `AilmentChance.Effect` is a Source-side effect at `FINAL_DAMAGE`, and its gate ends
  
          && (effect.getAttackType().isHit() || effect.getAttackType() == AttackType.bonus_dmg)
  
      — checked against the 6.4.13 jar, not only the fork. `bonus_dmg` is exactly what
      `buildBonusElementEvent` stamps on a converted element, and that child runs the whole sweep
      (`calcSourceEffects = !takenAs`), so the cold half of an 80%-converted physical hit is an
      ordinary cold hit as far as `freeze_chance` is concerned. Gating on `depth === 0` silently
      dropped it: the physical reading of Tailwind Sweep converts four fifths of itself to cold on
      a build with freeze chance and reported no ailment at all.
  
      The condition is `disableSourceStats`, which is the same thing the sweep above is gated on —
      a `damage_taken_as` child, or a self-hit that hit `no_attacker_stats_on_selfdmg`, never runs
      a Source-side effect and so cannot roll one either.
    */
    if (!ctx.disableSourceStats) {
        ailments.push(...applyAilments(ctx, shared.sourceSheet, shared.targetSheet, (ailment, base) => ailmentEventDamage(shared, ailment, base, diagnostics)));
    }
    if (recorder === undefined)
        return undefined;
    return {
        element,
        depth,
        takenAs,
        baseNumber: amount,
        steps,
        moreMultis,
        finalNumber: dealt,
        penetration: event.penetration,
        children,
    };
}
/**
 * `calculateEffects()` (EffectEvent.java:239-277).
 *
 *     effectsWithCtx.add(CALC_LAYERS_EFFECT);
 *     effectsWithCtx.sort(EffectWithCtx.COMPARATOR);
 *     for (EffectWithCtx item : effectsWithCtx) {
 *         if (item.stat.isNotZero() || item.effect.runsOnZeroStat()) {
 *             item.effect.TryModifyEffect(this, item.statSource, item.stat, item.stat.GetStat());
 *         }
 *     }
 *
 * The flush is a pseudo-stat sitting at priority 30 in the same list, not a step after it —
 * magic shield and ailments depend on the damage being final by the time they run.
 *
 * `EffectWithCtx.compare` returns 0 on equal priority and the underlying collection is a
 * `HashMap`, so the game's order within a band is genuinely unspecified. The engine sorts ties
 * by stat id: a defined order the game does not have is better than an arbitrary one, and it
 * is the only way two runs can be compared at all.
 */
export function sweep(ctx, sides, steps, moreMultis, 
/**
 * Which `mmorpg_stat` event this is. `on_damage` for a hit; `on_restore_resource` for the
 * once-a-second regen tick, which runs the same layers over a different number.
 */
eventId = ON_DAMAGE, 
/**
 * Whether to queue the layer flush at the end.
 *
 * True for a real event. False for the `on_damage_init` pre-pass, which exists only to let
 * `disable_attacker_stats` answer before the hit's effect list is built — flushing there would
 * spend the layers on a number no layer has written to yet.
 */
flushLayers = true) {
    const queue = [];
    for (const { side, sheet } of sides) {
        for (const [statId, stat] of sheet) {
            const def = ctx.index.get(statId);
            // Datapack `effect` blocks.
            for (const block of def?.effects ?? []) {
                if (block.side !== side)
                    continue;
                if (!block.events.includes(eventId))
                    continue;
                if (isZero(stat))
                    continue;
                queue.push({
                    priority: datapackPriority(block.order) ?? UNKNOWN_ORDER_PRIORITY,
                    statId,
                    side,
                    run: () => runBlock(ctx, statId, stat, def?.element, block, side),
                });
            }
            // In-code effects.
            for (const effect of IN_CODE_BY_STAT.get(statId) ?? []) {
                if (effect.side !== side)
                    continue;
                if ((effect.event ?? ON_DAMAGE) !== eventId)
                    continue;
                if (isZero(stat) && !effect.runsOnZero)
                    continue;
                queue.push({
                    priority: effect.priority,
                    statId,
                    side,
                    run: () => effect.run(ctx, stat.value, stat.dmgMulti),
                });
            }
        }
    }
    if (flushLayers) {
        queue.push({
            priority: PRIORITY.CALC_DAMAGE_LAYERS,
            statId: " flush",
            run: () => flush(ctx, steps, moreMultis),
        });
    }
    queue.sort((a, b) => (a.priority === b.priority ? a.statId.localeCompare(b.statId) : a.priority - b.priority));
    const recorder = ctx.event.recorder;
    for (const item of queue) {
        if (ctx.event.data.isCanceled())
            return;
        // Naming the stat here rather than at each write site is what keeps the breakdown from
        // leaking into every effect signature: a layer write knows who made it because the sweep
        // just said so.
        if (recorder !== undefined) {
            recorder.statId = item.statId;
            recorder.effectId = undefined;
            recorder.side = item.side;
        }
        // An avoided hit zeroes the number; the remaining stats still run, exactly as in game.
        item.run();
    }
    if (recorder !== undefined) {
        recorder.statId = undefined;
        recorder.effectId = undefined;
        recorder.side = undefined;
    }
}
/**
 * `StatData.isNotZero()`, negated.
 *
 *     public boolean isNotZero() {
 *         return v1 != 0 || this.m != 1;
 *     }
 *
 * — StatData.java:64-66, and `EffectEvent.calculateEffects` uses it as the gate on running a
 * stat's effects at all. **Both** halves matter, and the second is the one that bites: a
 * `MULTIPLICATIVE_DAMAGE` stat carries everything it has in `m` and leaves `v1` at zero, so
 * checking the value alone silently dropped every one of them. On the reference build that is
 * `all_fire_damage` at `m = 1.40` against a fire skill — a quarter of the damage, missing, with
 * nothing on screen to say so.
 */
function isZero(stat) {
    return stat.value === 0 && stat.dmgMulti === 1;
}
function runBlock(ctx, statId, stat, element, block, side) {
    const weight = evaluateIfs(ctx, block.ifs, { statId, value: stat.value, element }, side);
    if (weight <= 0)
        return;
    const shape = ctx.index.shapeOf(statId);
    for (const effectId of block.effects) {
        if (ctx.event.recorder !== undefined)
            ctx.event.recorder.effectId = effectId;
        applyStatEffect(ctx, effectId, { statId, value: stat.value, dmgMulti: stat.dmgMulti, multiUseType: shape.multiUseType }, side, weight);
    }
}
/**
 * `calculateStatLayersAndMoreMultis()` (EffectEvent.java:219-237).
 *
 * Layers apply in layer-priority order; every accumulated more-multi then applies
 * unconditionally, after all of them. `appliedFlatDamage` is captured on the way past because
 * ailments read it to reconstruct the pre-multiplier hit.
 */
function flush(ctx, steps, moreMultis) {
    const event = ctx.event;
    const recorder = event.recorder;
    if (!event.hasLayers())
        return;
    for (const layer of event.sortedLayers()) {
        // Read the accumulator before applying: `CONVERT_PERCENT` spends the conversion budget and
        // queues bonus events as it goes, so afterwards the layer no longer describes what it did.
        const before = event.data.getNumber(EVENT.NUMBER);
        const snapshot = recorder === undefined
            ? undefined
            : {
                amount: layer.getNumber(),
                multiplier: layer.layer.action === "MULTIPLY" ? layer.getMultiplier() : undefined,
                conversion: [...layer.normalizedConversion()].map(([element, percent]) => ({
                    element,
                    percent,
                })),
            };
        applyLayer(ctx, layer);
        if (layer.numberId === EVENT.NUMBER && layer.layer.id === "flat_damage") {
            event.appliedFlatDamage = layer.getNumber();
        }
        if (layer.numberId === EVENT.NUMBER && layer.layer.id === LAYER.DAMAGE_BLOCK) {
            event.hitChance = layer.getMultiplier();
        }
        if (recorder !== undefined && snapshot !== undefined) {
            steps.push({
                layerId: layer.layer.id,
                action: layer.layer.action,
                numberId: layer.numberId,
                side: layer.side,
                amount: snapshot.amount,
                multiplier: snapshot.multiplier,
                conversion: snapshot.conversion,
                additionalTo: layer.additionalTo,
                before,
                after: event.data.getNumber(EVENT.NUMBER),
                contributions: recorder.forLayer(layer.layer.id, layer.numberId, layer.side, layer.additionalTo),
            });
        }
    }
    for (const more of event.moreMultis) {
        const before = event.data.getNumber(more.numberId);
        const after = before * more.multi;
        event.data.setNumber(more.numberId, after);
        if (recorder !== undefined) {
            moreMultis.push({
                statId: more.statId,
                numberId: more.numberId,
                multi: more.multi,
                ...(more.effectId === undefined ? {} : { effectId: more.effectId }),
                before,
                after,
            });
        }
    }
}
/** The five `LayerAction`s (StatLayer.java:50-171). */
function applyLayer(ctx, layer) {
    const event = ctx.event;
    const data = event.data;
    switch (layer.layer.action) {
        case "ADD":
            data.addNumber(layer.numberId, layer.getNumber());
            return;
        case "MULTIPLY":
            data.setNumber(layer.numberId, data.getNumber(layer.numberId) * layer.getMultiplier());
            return;
        case "CONVERT_PERCENT": {
            // `original` is snapshotted once, before any conversion runs, so two conversions each
            // taking 30% take 30% of the *same* number rather than compounding.
            if (!data.isNumberSetup(EVENT.BEFORE_CONVERSION_NUMBER)) {
                data.setupNumber(EVENT.BEFORE_CONVERSION_NUMBER, data.getNumber(EVENT.NUMBER));
            }
            const original = data.getNumber(EVENT.BEFORE_CONVERSION_NUMBER);
            for (const [element, raw] of layer.normalizedConversion()) {
                let conv = Math.min(raw, 100);
                if (conv > event.unconvertedDamagePercent)
                    conv = event.unconvertedDamagePercent;
                // `return`, not `continue` — the Java aborts the whole loop once the budget runs out.
                if (conv <= 0)
                    return;
                event.unconvertedDamagePercent -= conv;
                const dmg = clampTo((original * conv) / 100, 0, original);
                if (dmg > 0) {
                    // `addBonusEleDmg`, not a bare push: converting to the element the hit is already
                    // dealt as puts the damage back on this event's `flat_damage` layer rather than
                    // spawning a child that would re-sweep the attacker's stats.
                    event.addBonusEleDmg(element, dmg, layer.side);
                    data.addNumber(EVENT.NUMBER, -dmg);
                }
            }
            return;
        }
        case "DAMAGE_TAKEN_AS": {
            const original = data.getNumber(EVENT.NUMBER);
            for (const [element, raw] of layer.normalizedConversion()) {
                // "taking an element as itself changes nothing, and moving it would only lose the
                // damage - this layer runs long after FLAT_DAMAGE was already applied"
                if (element === data.getElement())
                    continue;
                let conv = Math.min(raw, 100);
                if (conv > event.unconvertedDamageTakenAsPercent)
                    conv = event.unconvertedDamageTakenAsPercent;
                if (conv <= 0)
                    return;
                event.unconvertedDamageTakenAsPercent -= conv;
                const dmg = clampTo((original * conv) / 100, 0, original);
                if (dmg > 0) {
                    event.addDamageTakenAsEleDmg(element, dmg);
                    data.addNumber(EVENT.NUMBER, -dmg);
                }
            }
            return;
        }
        case "X_AS_BONUS_Y_ELEMENT_DAMAGE": {
            const conv = layer.number;
            if (conv <= 0 || layer.additionalTo === undefined)
                return;
            const dmg = Math.max((data.getNumber(EVENT.NUMBER) * conv) / 100, 0);
            if (dmg > 0) {
                event.addBonusEleDmg(layer.additionalTo, dmg, layer.side);
            }
            return;
        }
    }
}
function collectBonusElements(ctx, event, shared, crit, depth, byElement, ailments, diagnostics, procs, restores, key) {
    const traces = [];
    for (const [i, bonus] of event.bonusElements.entries()) {
        const trace = runEvent(shared, bonus.element, bonus.amount, crit, depth + 1, bonus.takenAs, byElement, ailments, diagnostics, 
        // Collected under the child's own key and folded in by `procsPerHit`. The child is its own
        // `DamageEvent` and rolls every proc block again — and its own crit — and an
        // `ele_match_stat` gate can only pass on it: Blade of Desecrated Hallows' Frost Orbs proc
        // on a *cold* hit, and a physical attack it converts is cold only in the bonus event.
        procs, 
        // Leech is the opposite case, and deliberately so: each bonus element is its own
        // `DamageEvent` carrying its own share of the hit, and `ele_match_stat` picks the leech
        // stat that matches *it*. Collecting from the children is how `fire_mana_leech` reaches
        // the fire half of a converted physical hit. A `damage_taken_as` child skips the source
        // sweep entirely (`disableSourceStats`), so it contributes nothing here either way.
        restores, `${key}/${i}:${bonus.element}${bonus.takenAs ? "~" : ""}`);
        if (trace !== undefined)
            traces.push(trace);
    }
    return traces;
}
// ---------------------------------------------------------------------------
// Inputs
// ---------------------------------------------------------------------------
const IN_CODE = inCodeEffects();
/**
 * The same effects keyed on the stat they belong to.
 *
 * The sweep asks "does this stat have an in-code effect?" once per stat per side, and the sheet
 * carries several hundred stats — walking the whole list each time is a six-figure loop for one
 * hit, and the regeneration and leech ticks sweep too. The list never changes, so it is indexed
 * once at module load.
 */
const IN_CODE_BY_STAT = (() => {
    const map = new Map();
    for (const effect of IN_CODE) {
        const list = map.get(effect.statId);
        if (list)
            list.push(effect);
        else
            map.set(effect.statId, [effect]);
    }
    return map;
})();
/**
 * The declared enemy, as a stat sheet.
 *
 * Every field the document omits reads 0, which is what an undeclared enemy stat means in the
 * game too — an unset stat resolves to `StatData.empty()`.
 */
function enemySheet(enemy) {
    const sheet = new Map();
    const put = (statId, value) => {
        sheet.set(statId, { value, dmgMulti: 1, hardcap: 0, softcap: 0 });
    };
    // Seeded at 0 whether or not the document mentions them, because the game's `Unit` holds
    // every registered stat and the sweep is driven by that map. `ArmorEffect` and
    // `ElementalResistEffect` both declare `runsOnZeroStat()`, and both *spend* the attacker's
    // penetration — so an undeclared defence is not a defence the attacker gets to skip, it is
    // one worth 0 that still eats penetration. Leaving them out silently discarded it.
    put("armor", 0);
    for (const element of SINGLE_ELEMENTS)
        put(`${element.guid}_resist`, 0);
    if (!enemy)
        return sheet;
    if (enemy.armor !== undefined)
        put("armor", enemy.armor);
    if (enemy.blockChance !== undefined)
        put("block_chance", enemy.blockChance);
    if (enemy.dodge !== undefined)
        put("dodge", enemy.dodge);
    // `spell_dodge` is its own stat and its own curve (`valueNeededAtLevelOne: 200`, against
    // dodge's 100), so a spell is evaded by a different number than an attack is. The pack gives
    // every mob both.
    if (enemy.spellDodge !== undefined)
        put("spell_dodge", enemy.spellDodge);
    if (enemy.damageReduction !== undefined)
        put("damage_reduction", enemy.damageReduction);
    for (const [guid, value] of Object.entries(enemy.resists ?? {}))
        put(`${guid}_resist`, value);
    for (const [guid, value] of Object.entries(enemy.maxResists ?? {}))
        put(`max_${guid}_resist`, value);
    return sheet;
}
/**
 * Debuffs the build is assumed to have put on the mob, folded into its sheet.
 *
 * This is the half of the effect state that moves a damage number: `shred` is `PERCENT -8 armor`
 * stacking to ten, `elemental_weakness` — what the pack calls Scorched — is
 * `FLAT -25 elemental_resist`. Both sit on the *target*, so applying them means editing the
 * enemy's sheet before the mitigation layers read it, not the character's.
 *
 * Two things make this more than an addition.
 *
 * **Modifier types.** `InCalcStatData` resolves `(base + Flat) × (1 + Percent/100) × Multi`, and
 * a debuff that reads `-8%` is a PERCENT — adding its value to an armour of 4000 would take off
 * eight points instead of a fifth. The enemy's declared value stands in for `base`, and the rest
 * is the container's own arithmetic.
 *
 * **Aggregate resists.** The mitigation layers run one effect per *single* element —
 * `fire_resist`, `water_resist`, `lightning_resist` — and skip `Elemental` and `ALL` entirely. A
 * debuff written against `elemental_resist` would land on a stat nothing reads, so it is expanded
 * the way `Elements.getAllSingleElemental()` groups them.
 *
 * **Strength.** A debuff is interpolated and multiplied exactly as a buff is, and reading either
 * at its floor understates it. `EffectOption` already carries both halves, derived once:
 * `rollPercent` from the rank of the spell that applies it — `hunters_mark` is `MORE 7.5..15` on
 * `dmg_received`, a factor of two between a rank-1 and a maxed one — and `strMulti` from your
 * `inc_effect_of_<tag>_buff_given` stats. For a debuff that multiplier is the `_given` half only:
 * the `_on_you` half is the mob's, and in this pack it is `MobRarity.stats`'s single
 * `inc_effect_of_negative_buff_on_you`, which a declared enemy does not carry.
 */
/**
 * The enemy's sheet with everything aimed at it folded in — its affixes and your debuffs.
 *
 * Both in one accumulator on purpose; see {@link applySheetMods}.
 */
function targetSheetFor(build, effects, snapshot, index, bal, report, 
/** Filled in with where each modified stat came from, when a caller wants to explain one. */
origins) {
    const sheet = enemySheet(build.config?.enemy);
    const affixIds = build.config?.enemy?.affixes ?? [];
    // `Load.Unit(en).getLevel()` — the mob's own level, which falls back to the character's when
    // the document does not say, the same way every other enemy field does.
    const mobLevel = build.config?.enemy?.level ?? build.config?.enemyLevel ?? build.character.level;
    // Through `targetStats` like a debuff's: `of_elemental_resistance` writes `elemental_resist`,
    // which no mitigation effect reads — the aggregate has to be handed to the elements it covers
    // or the affix moves nothing at all.
    const affixes = mobAffixMods(snapshot, index, bal, mobLevel, affixIds, report).flatMap((m) => spreadsTo(m.statId).map((statId) => ({
        statId,
        type: m.type,
        value: m.value,
        source: m.source,
        path: "config.enemy.affixes",
    })));
    for (const d of mobAffixDiagnostics(snapshot, affixIds))
        report(d.severity, d.code, d.path, d.message);
    // The map's `Mobs` affixes and its tier, which every mob in it carries on top of its own — see
    // `damage/map.ts`. Same accumulator as the affixes above, because the game sums them in one
    // container: `fire_res 45` from the map and `of_elemental_resistance` from the mob meet there.
    const fromMap = mapMobMods(snapshot, index, bal, build.config?.map, mobLevel, report).flatMap((m) => spreadsTo(m.statId).map((statId) => ({
        statId,
        type: m.type,
        value: m.value,
        source: m.source,
        path: "config.map",
    })));
    const mods = [
        ...affixes,
        ...fromMap,
        ...debuffMods(effects, snapshot, index, bal, build.character.level),
    ];
    // Captured before the mods are folded in, because that is the only moment the enemy's own
    // declared value still exists — `applySheetMods` overwrites it in place.
    if (origins !== undefined) {
        for (const mod of mods) {
            let origin = origins.get(mod.statId);
            if (origin === undefined) {
                origin = {
                    statId: mod.statId,
                    declared: sheet.get(mod.statId)?.value ?? 0,
                    mods: [],
                    final: 0,
                };
                origins.set(mod.statId, origin);
            }
            origin.mods.push(mod);
        }
    }
    applySheetMods(sheet, mods, index, report);
    if (origins !== undefined) {
        for (const origin of origins.values()) {
            origin.final = sheet.get(origin.statId)?.value ?? 0;
        }
    }
    return sheet;
}
function debuffMods(effects, snapshot, index, bal, level) {
    const out = [];
    for (const option of activeOn(effects, "target")) {
        const data = entry(snapshot, CATEGORY.exileEffect, option.id)?.data;
        const raw = data?.["stats"];
        if (!Array.isArray(raw))
            continue;
        for (const mod of parseRolledMods(raw)) {
            const exact = rollToExact(mod, option.rollPercent, level, index.shapeOf(mod.statId), bal);
            // `stacks_affect_stats` multiplies the value by the stack count — `increaseByAddedPercent`
            // with `(stacks - 1) * 100`, which is exactly `× stacks`.
            const stacked = data?.["stacks_affect_stats"] === true && option.stacks > 1
                ? multiplyExact(exact, option.stacks)
                : exact;
            // `ExileEffectInstanceData.str_multi`, the same factor `collectExileEffects` applies to a
            // buff. A character stacked into "increased effect of negative buffs given" really does
            // shred harder, and until now that stat changed nothing.
            const scaled = option.strMulti === 1 ? stacked : multiplyExact(stacked, option.strMulti);
            for (const statId of spreadsTo(scaled.statId)) {
                out.push({ statId, type: scaled.type, value: scaled.value, source: option.id, path: "config.effects" });
            }
        }
    }
    return out;
}
/**
 * Everything aimed at the enemy, folded into its sheet in one pass.
 *
 * One pass rather than one per source, because the container does not apply contexts in turn:
 * `InCalcStatData.calcValue` is `(base + Flat) × (1 + Percent/100) × Multi` over the *sum* of
 * every context that touched the stat. A mob affix granting `MORE 20 accuracy` and a debuff
 * granting `PERCENT -8 armor` have to meet in the same accumulator or the arithmetic is a
 * different one.
 */
export function applySheetMods(sheet, mods, index, report) {
    // Which source wrote to a stat, so one that turns out to change nothing can be named.
    const wroteTo = new Map();
    const pending = new Map();
    for (const mod of mods) {
        let into = pending.get(mod.statId);
        if (!into) {
            into = { flat: 0, percent: 0, multi: 1 };
            pending.set(mod.statId, into);
        }
        wroteTo.set(mod.statId, { source: mod.source, path: mod.path });
        if (mod.type === "FLAT")
            into.flat += mod.value;
        else if (mod.type === "PERCENT")
            into.percent += mod.value;
        else
            into.multi *= 1 + mod.value / 100;
    }
    for (const [statId, mods2] of pending) {
        const mods = mods2;
        const shape = index.shapeOf(statId);
        const current = sheet.get(statId);
        // The declared enemy stat stands in for `Stat.base`: an undeclared defence is 0, which is
        // what `StatData.empty()` gives and what `enemySheet` already seeds.
        let value = (current?.value ?? 0) + mods.flat;
        value *= 1 + mods.percent / 100;
        if (shape.multiUseType === "MULTIPLY_STAT")
            value *= mods.multi;
        // The other half of `InCalcStatData`: on a `MULTIPLICATIVE_DAMAGE` stat, MORE is not folded
        // into the value at all but kept as the stat's damage multiplier, which the damage effect
        // spends as its own MORE line. A map tier's `MORE total_damage` on a mob with no flat
        // `total_damage` is exactly this, and dropping it here lost the whole tier.
        const damageMulti = shape.multiUseType === "MULTIPLICATIVE_DAMAGE" ? mods.multi : 1;
        // `InCalcStatData.calcValue` is `(base + Flat) × (1 + Percent/100) × Multi`, and MORE feeds
        // only `Multi` — so a MORE modifier on a stat whose base and flat are both zero multiplies
        // zero and produces zero. That is the game's arithmetic, not a shortcut taken here, and it
        // makes a debuff that reads as a large effect worth literally nothing. `hunters_mark` is the
        // one that matters: `MORE 7.5..15 dmg_received`, and `dmg_received` has `base: 0`, so
        // marking a mob changes no number at all unless something else has already given it some.
        if (mods.multi !== 1 && damageMulti === 1 && (current?.value ?? 0) + mods.flat === 0) {
            report("warning", "debuff-more-on-zero-base", wroteTo.get(statId)?.path ?? "config.effects", `\`${wroteTo.get(statId)?.source ?? "a debuff"}\` modifies \`${statId}\` by MORE, and the target's ` +
                `\`${statId}\` is 0. \`InCalcStatData\` folds MORE into a multiplier over ` +
                `\`(base + flat) × (1 + percent/100)\`, so it multiplies zero: the debuff changes ` +
                `nothing, in the engine and in the game alike. Give the enemy a non-zero \`${statId}\` ` +
                `if you are modelling a mob that has one.`);
        }
        sheet.set(statId, {
            value: Math.min(Math.max(value, shape.min), shape.max),
            dmgMulti: (current?.dmgMulti ?? 1) * damageMulti,
            hardcap: current?.hardcap ?? 0,
            softcap: current?.softcap ?? 0,
        });
    }
}
/**
 * Which stats a modifier written against an aggregate really lands on.
 *
 * Every damage layer is registered per *single* element — `fire_resist`, `water_penetration` —
 * and the aggregates are not layers at all. In a real calculation that is fine, because
 * `ITransferToOtherStats` empties `elemental_resist` into its three elements before the first
 * pass and clears itself (`applyTransfers` in `calculate.ts`). A sheet assembled by hand never
 * ran that pass, so on those an aggregate is an id nothing reads and the modifier is silently
 * worth nothing.
 *
 * Driven by `CODE_ONLY_TRANSFERS` rather than by a list written here, which is the fix for the
 * bug that prompted this: the hand-written version covered `elemental_resist` and `all_resist`
 * and nothing else, so the `penetrating` mob affix's `elemental_penetration 25` reached none of
 * your three elemental resists — its `armor_penetration` and `chaos_penetration` were real ids
 * and did apply, which is exactly the "physical and chaos move, the elements do not" shape it
 * showed up as. The generated table is the game's own `ITransferToOtherStats` and covers the
 * whole family: both penetrations, both conversions, `phys_taken_as_elemental` and the two
 * resist aggregates.
 *
 * `all_resist` stays hardcoded because it is not an `ITransferToOtherStats` at all — the ALL
 * element spreads through `Elements.multi`, a different mechanism — and dropping it here would
 * silently un-fix a case that already worked.
 */
function spreadsTo(statId) {
    if (statId === "all_resist")
        return SINGLE_ELEMENTS.map((e) => `${e.guid}_resist`);
    return CODE_ONLY_TRANSFERS[statId] ?? [statId];
}
/**
 * The same, for a sheet that is not the target's.
 *
 * Exported for `defence.ts`, which builds the *attacker's* sheet by hand from a mob's affixes
 * and hit the identical problem from the other side.
 */
export function aggregateSpread(statId) {
    return spreadsTo(statId);
}
/** The first `damage` act found anywhere in the spell's component tree. */
function damageAct(spell, options) {
    if (options.valueCalcId !== undefined && options.element !== undefined) {
        return { element: options.element, valueCalcId: options.valueCalcId };
    }
    let found;
    const visit = (node) => {
        if (found)
            return;
        if (Array.isArray(node)) {
            for (const item of node)
                visit(item);
            return;
        }
        const obj = asObject(node);
        if (!obj)
            return;
        if (obj["type"] === "damage") {
            const map = asObject(obj["map"]) ?? {};
            const element = typeof map["element"] === "string" ? map["element"] : "Physical";
            found = {
                element: element in ELEMENTS ? element : "Physical",
                valueCalcId: typeof map["value_calculation"] === "string" ? map["value_calculation"] : "",
            };
            return;
        }
        for (const value of Object.values(obj))
            visit(value);
    };
    visit(spell["attached"]);
    return found;
}
function mainSkill(build, report) {
    const skills = build.skills ?? [];
    const marked = skills.find((s) => s.main === true);
    if (marked)
        return marked;
    if (skills.length > 1) {
        report("warning", "no-main-skill", "skills", `${skills.length} skills are declared and none is marked \`main\`, so \`${skills[0]?.spellId}\` was used.`);
    }
    return skills[0];
}
function spellTags(spell) {
    const config = asObject(spell["config"]);
    const tagsNode = asObject(config?.["tags"]);
    const list = tagsNode?.["tags"];
    return new Set(Array.isArray(list) ? list.filter((t) => typeof t === "string") : []);
}
/**
 * One swing of the weapon in your hand.
 *
 * `WeaponMechanic.doNormalAttack` is four lines and every one of them matters:
 *
 *     int num = (int) data.getAttackerEntityData().getUnit()
 *             .getCalculatedStat(WeaponDamage.getInstance()).getValue();
 *     DamageEvent dmg = EventBuilder.ofDamage(data, attacker, target, num)
 *             .setupDamage(AttackType.hit, weptype, data.weaponData.GetBaseGearType().style)
 *             .setIsBasicAttack()
 *             .build();
 *     dmg.Activate();
 *
 * So a basic attack is not a special pipeline. It is the *same* `DamageEvent` a spell raises,
 * with three differences: the base is the `weapon_damage` stat truncated to an int rather than a
 * `value_calculation`; `dmg_effectiveness` is 1, because nothing scales it; and the sheet it
 * sweeps is the **character unit**, not a spell unit — there is no spell, so there are no
 * support gems and no `statsForSkillGem`.
 *
 * `EventData.SPELL` is left empty for the same reason, which is what makes every `spell_has_tag`
 * gate correctly fail on a swing.
 */
export function simulateBasicAttack(build, snapshot, options = {}) {
    const diagnostics = [];
    const report = (severity, code, path, message) => {
        diagnostics.push({ severity, code, path, message });
    };
    const index = statIndex(snapshot);
    const layers = layerIndex(snapshot);
    const bal = balance(snapshot, options.balanceId);
    const compat = options.compat ?? ORIGINAL_MODE;
    const sheetOptions = {
        ...pick(options),
        ...(options.effects === undefined ? {} : { effects: options.effects }),
    };
    const characterRun = options.sheets?.character ?? calculate(build, snapshot, sheetOptions);
    const characterSheet = characterRun.stats;
    const effects = options.effects ?? characterRun.effects;
    const targetOrigins = new Map();
    const targetSheet = targetSheetFor(build, effects, snapshot, index, bal, report, targetOrigins);
    // `(int)` on the stat value, toward zero, exactly as the Java casts it.
    const baseValue = Math.trunc(sheetValue(characterSheet, "weapon_damage"));
    if (baseValue <= 0) {
        report("info", "no-weapon-damage", "gear", "This build's `weapon_damage` is zero, so a basic attack deals nothing. It is the stat a " +
            "weapon's own damage roll lands on, so a character holding no weapon reads zero here.");
    }
    // `AttackType.hit`, `Elements.Physical` — a swing's element is the weapon's, and every weapon
    // in this pack rolls physical. Conversion stats move it afterwards like any other hit.
    const element = options.element ?? "Physical";
    const critChance = clamp01(sheetValue(characterSheet, "critical_hit") / 100);
    const shared = {
        snapshot,
        index,
        layers,
        balance: bal,
        compat,
        build,
        // No spell: `getSpellData("")` is how the game's own empty `InsertedSpell` reads, and an
        // empty id is what makes `spell_has_tag_*` and `is_spell` answer false.
        skill: { spellId: "" },
        spell: {},
        sourceSheet: characterSheet,
        targetSheet,
        baseValue,
        // `dmg_effectiveness` is a `value_calculation` field and a swing has no value calculation.
        effectiveness: 1,
        element,
        effects,
        breakdown: options.breakdown === true,
        basicAttack: true,
        summonAttack: false,
        style: playStyle(snapshot, build),
        // A swing always lands on something else; nothing in the pack makes you hit yourself with one.
        selfHit: false,
        sourceStatsDisabled: false,
        hitChance: 1,
    };
    const procBranches = options.procs === true ? { hit: new Map(), crit: new Map() } : undefined;
    const hit = runBranch(shared, false, diagnostics, procBranches?.hit);
    const crit = runBranch(shared, true, diagnostics, procBranches?.crit);
    const procs = procBranches === undefined ? undefined : procsPerHit(procBranches.hit, procBranches.crit, critChance);
    return {
        spellId: "",
        baseValue,
        dmgEffectiveness: 1,
        element,
        critChance,
        // A swing is `AttackType.hit` and not a `magic` spell, which is exactly what `DodgeRating`
        // takes, so against a mob with dodge the figure is routinely below 1.
        hitChance: shared.hitChance,
        selfHit: false,
        hit,
        crit,
        average: blend(hit, crit, critChance),
        // Both halves are the character sheet: a swing has no spell unit, and handing back a second
        // reference to the same run is more honest than inventing one.
        sheets: { character: characterRun, spell: characterRun },
        target: { sheet: targetSheet, origins: targetOrigins },
        ...(procs === undefined ? {} : { procs }),
        diagnostics,
    };
}
/**
 * The ailment's own `DamageEvent`, run purely to arrive at a number.
 *
 *     EventBuilder.ofDamage(source, target, dmg)
 *             .setupDamage(AttackType.dot, WeaponTypes.none, PlayStyle.INT)
 *             .set(x -> { x.disableActivation = true;
 *                         x.setElement(ailment.element);
 *                         x.setisAilmentDamage(ailment);
 *                         if (spell != null) x.data.setString(EventData.SPELL, spell.GUID()); })
 *
 * — AilmentChance.activate. Everything about its shape is the builder's, not the hit's, and each
 * of those differences changes which stats it picks up:
 *
 *  - **`AttackType.dot`** — nothing gated on a hit applies, and `AilmentChance.canActivate`
 *    refuses it, so an ailment can never inflict an ailment. That is the recursion guard, and it
 *    is the game's rather than one invented here.
 *  - **`WeaponTypes.none` and `PlayStyle.INT`** — written as literals. A greatsword build's
 *    ailments are `int`, which is what stops the thirteen `style_is_int_is_false` stats
 *    (`attack_damage`, the three leeches, the four attack procs) from reaching a DoT.
 *  - **No crit.** The builder never sets it, so the pinned branch does not carry over; an
 *    ailment inflicted by a crit is the same size as one inflicted by a normal hit.
 *
 * Both sides are swept, because `Activate()` is the whole pipeline: the target's mitigation
 * layers apply here, and `<ailment>_resistance` applies again afterwards in
 * `onAilmentCausingDamage`. Both, in that order, is what the game does.
 *
 * A trace is recorded when one was asked for, because the game prints this event as its own
 * block in the damage log and the rows are the whole point: the ailment's `additive_damage` is a
 * different number from the hit's — `dot`/`int` against `hit`/the weapon's style — and only a
 * row-by-row reading says which stats crossed over and which did not. It is still off by default,
 * so the ordinary path allocates nothing.
 */
function ailmentEventDamage(shared, ailment, base, diagnostics) {
    if (base <= 0)
        return { damage: 0 };
    const recorder = shared.breakdown ? new Recorder() : undefined;
    const event = new DamageEventState(shared.layers, undefined, recorder);
    event.data.setupNumber(EVENT.NUMBER, base);
    // A `value_calculation`'s field, and this event has no value calculation.
    event.data.setupNumber(EVENT.DMG_EFFECTIVENESS, 1);
    event.data.setString(EVENT.ELEMENT, ailment.element);
    event.data.setString(EVENT.ATTACK_TYPE, "dot");
    event.data.setString(EVENT.WEAPON_TYPE, "none");
    event.data.setString(EVENT.STYLE, "int");
    event.data.setString(EVENT.SPELL, shared.skill.spellId);
    event.data.setString(EVENT.AILMENT, ailment.id);
    const ctx = {
        snapshot: shared.snapshot,
        index: shared.index,
        balance: shared.balance,
        compat: shared.compat,
        event,
        source: shared.sourceSheet,
        target: shared.targetSheet,
        sourceLevel: shared.build.character.level,
        targetLevel: shared.build.config?.enemy?.level ??
            shared.build.config?.enemyLevel ??
            shared.build.character.level,
        spell: shared.spell,
        spellId: shared.skill.spellId,
        spellTags: spellTags(shared.spell),
        config: shared.build.config ?? {},
        effects: shared.effects,
        diagnostics,
        report: (severity, code, path, message) => diagnostics.push({ severity, code, path, message }),
        reportedConditions: new Set(),
        reportedEffects: new Set(),
        pinnedBooleans: new Set([EVENT.CRIT]),
        disableSourceStats: false,
        // Carried from the hit that caused it: an ailment off a self-hit is one you gave yourself,
        // and its own sweep reads the same sheets on the same two sides.
        sourceIsTarget: shared.selfHit,
        sourceDualWielding: isDualWielding(shared.snapshot, shared.build.gear ?? []),
        ...(shared.selfHit ? { targetDualWielding: isDualWielding(shared.snapshot, shared.build.gear ?? []) } : {}),
    };
    const steps = [];
    const moreMultis = [];
    sweep(ctx, [
        { side: "Source", sheet: shared.sourceSheet },
        { side: "Target", sheet: shared.targetSheet },
    ], steps, moreMultis);
    const damage = Math.max(0, event.damage);
    if (recorder === undefined)
        return { damage };
    return {
        damage,
        trace: {
            element: ailment.element,
            // Its own event, not a child of the hit's: `AilmentChance.activate` builds it from the
            // source and target rather than from the hit, so nothing about it is nested.
            depth: 0,
            takenAs: false,
            baseNumber: base,
            steps,
            moreMultis,
            finalNumber: damage,
            penetration: event.penetration,
            // An ailment event cannot spawn bonus elements: `addBonusEleDmg` is reached only from
            // effects gated on a direct hit, and this one is `dot`.
            children: [],
        },
    };
}
/** `spell.config.style` — already `PlayStyle.id` in the snapshot, so lower case throughout. */
function spellStyle(spell) {
    const config = asObject(spell["config"]);
    const style = config?.["style"];
    return typeof style === "string" ? style.toLowerCase() : "";
}
/**
 * `PlayStyle.id` of the mainhand's base gear type — `str`, `dex` or `int`.
 *
 * `setupDamage` takes it from `data.weaponData.GetBaseGearType().style` on a basic attack and
 * from the spell's own config on a cast. The snapshot spells the gear type's `STR`; `PlayStyle.id`
 * is its lower case, which is what every `string_matches` on `style` compares against.
 */
function playStyle(snapshot, build) {
    for (const item of build.gear ?? []) {
        if (item.offhand === true)
            continue;
        const base = entry(snapshot, CATEGORY.baseGearType, item.base)?.data;
        const type = base?.["weapon_type"];
        if (typeof type !== "string" || type.length === 0 || type === "none")
            continue;
        const style = base?.["style"];
        if (typeof style === "string" && style.length > 0)
            return style.toLowerCase();
    }
    return "";
}
/** `EventData.WEAPON_TYPE`, set from the held weapon when the spell uses one. */
/**
 * The id the weapon multiplier is recorded under.
 *
 * Not a `mmorpg_stat` — the game passes a `Words` entry rather than a stat, because this is a
 * property of the weapon's slot and nothing on a sheet. It still has to be *named*, because the
 * damage log prints it as its own row and a breakdown that cannot say where a 1.6 came from is
 * the thing the trace exists to prevent.
 */
export const WEAPON_BASIC_ATTACK_MULTI = "weapon_basic_attack_dmg_multi";
/**
 * `GearSlot.getBasicDamageMulti()` for the weapon in hand, or `undefined` when unarmed.
 *
 * The same walk as {@link weaponType} and for the same reason: a build document has no "mainhand"
 * field, so the weapon is whichever equipped item has a weapon slot and is not flagged `offhand`.
 * The first match wins, which is what the game does too — a swing uses the mainhand.
 */
function basicAttackWeaponMulti(shared) {
    for (const item of shared.build.gear ?? []) {
        if (item.offhand === true)
            continue;
        const base = entry(shared.snapshot, CATEGORY.baseGearType, item.base)?.data;
        const slotId = base?.["gear_slot"];
        if (typeof slotId !== "string" || slotId.length === 0)
            continue;
        const slot = entry(shared.snapshot, CATEGORY.gearSlot, slotId)?.data;
        const weaponData = slot?.["weapon_data"];
        if (weaponData === null || typeof weaponData !== "object" || Array.isArray(weaponData))
            continue;
        const multi = weaponData["damage_multiplier"];
        // A zero multiplier is every non-weapon slot, so it is "not a weapon" rather than "a weapon
        // that deals nothing" — skip and keep looking rather than returning 0 and zeroing the swing.
        if (typeof multi === "number" && multi > 0)
            return multi;
    }
    return undefined;
}
function weaponType(shared) {
    for (const item of shared.build.gear ?? []) {
        if (item.offhand === true)
            continue;
        const base = entry(shared.snapshot, CATEGORY.baseGearType, item.base)?.data;
        const type = base?.["weapon_type"];
        if (typeof type === "string" && type.length > 0 && type !== "none")
            return type;
    }
    return "none";
}
/** Weighted mean of the two branches. Elements are blended individually so the split survives. */
function blend(hit, crit, critChance) {
    const byElement = new Map();
    for (const element of new Set([...hit.byElement.keys(), ...crit.byElement.keys()])) {
        const a = hit.byElement.get(element) ?? 0;
        const b = crit.byElement.get(element) ?? 0;
        byElement.set(element, a * (1 - critChance) + b * critChance);
    }
    let total = 0;
    for (const amount of byElement.values())
        total += amount;
    return {
        total,
        byElement,
        ailments: blendAilments(hit.ailments, crit.ailments, critChance),
        restores: blendRestores(hit.restores, crit.restores, critChance),
    };
}
/**
 * The two branches' ailments, weighted the same way the damage is.
 *
 * Keyed on the ailment rather than positionally, for the reason {@link blendRestores} is: the
 * crit branch can reach a block the non-crit one does not — anything gated on `is_crit` — so the
 * two lists are not parallel, and a crit-only ailment lined up against the wrong slot or fell off
 * the end.
 *
 * Every number that scales with the hit is weighted, not just `damagePerSecond`. Weighting one
 * and carrying the rest from whichever branch happened to be first is what put
 * "565/s for 3s = 1697" on the Average card with the three numbers not multiplying out — the
 * per-second figure blended and the total beside it the non-crit branch's.
 *
 * `chance` and `durationSeconds` are properties of the ailment rather than of the hit —
 * `<ailment>_chance` and the duration stat are read off the same sheet in both branches — so
 * they are taken from whichever branch has the row rather than averaged.
 */
function blendAilments(hit, crit, critChance) {
    const merged = new Map();
    const fold = (results, weight) => {
        for (const result of results) {
            const found = merged.get(result.ailment);
            if (found) {
                found.damagePerSecond += result.damagePerSecond * weight;
                found.totalDamage += result.totalDamage * weight;
                found.accumulated += result.accumulated * weight;
            }
            else {
                merged.set(result.ailment, {
                    ...result,
                    damagePerSecond: result.damagePerSecond * weight,
                    totalDamage: result.totalDamage * weight,
                    accumulated: result.accumulated * weight,
                });
            }
        }
    };
    fold(hit, 1 - critChance);
    fold(crit, critChance);
    return [...merged.values()];
}
/**
 * The two branches' leech, weighted the same way the damage is.
 *
 * Keyed on stat and resource rather than positionally: the crit branch can reach a block the
 * non-crit one does not (anything gated on `is_crit`), so the lists are not parallel. A record
 * present in only one branch is carried at its own branch's weight, which is the whole point —
 * crit-only leech should read as `chance × amount`, not as the full amount.
 */
function blendRestores(hit, crit, critChance) {
    const merged = new Map();
    const fold = (records, weight) => {
        for (const record of records) {
            const key = `${record.statId}|${record.effectId}|${record.resource}|${record.restoreType}`;
            const found = merged.get(key);
            if (found)
                found.amount += record.amount * weight;
            else
                merged.set(key, { ...record, amount: record.amount * weight });
        }
    };
    fold(hit, 1 - critChance);
    fold(crit, critChance);
    return [...merged.values()];
}
function envOf(base, index, bal) {
    return {
        snapshot: base.snapshot,
        index,
        balance: bal,
        level: base.level,
        diagnostics: [],
        report: () => { },
    };
}
function pick(options) {
    return {
        ...(options.balanceId === undefined ? {} : { balanceId: options.balanceId }),
        ...(options.baseStatsId === undefined ? {} : { baseStatsId: options.baseStatsId }),
    };
}
function asObject(value) {
    return value !== null && typeof value === "object" && !Array.isArray(value)
        ? value
        : undefined;
}
function clampTo(value, min, max) {
    return value < min ? min : value > max ? max : value;
}
function clamp01(value) {
    return clampTo(value, 0, 1);
}
//# sourceMappingURL=simulate.js.map